<script setup lang="ts">import { ref as _ref } from 'vue';

import type GiftTemplate from '@consumer/types/GiftTemplate'
import { preventNonNumericInput } from '@consumer/utils/number'
import type { MultiplaceGift } from '@consumer/types/serializers'
import GInputError from '@consumer/components/GInputError.vue'

withDefaults(defineProps<{
  gift: GiftTemplate
  multiplaceGift: MultiplaceGift
}>(), {  })

const lowerValue = _ref(__props.multiplaceGift.amounts[0])
const middleValue = _ref(__props.multiplaceGift.amounts[1])
const higherValue = _ref(__props.multiplaceGift.amounts[2])
let errors = _ref([] as string[])

const MINIMUM_AMOUNT = 10
const MAXIMUM_AMOUNT = 1000

function isSequential () {
  return lowerValue.value < middleValue.value && middleValue.value < higherValue.value
}

function greaterThanMin () {
  return lowerValue.value >= MINIMUM_AMOUNT
}

function lessThanMax () {
  return higherValue.value <= MAXIMUM_AMOUNT
}

function validates () {
  let validates = true
  errors.value = []

  if (!isSequential()) {
    validates = false
    errors.value.push('All amounts must increase in value')
  }

  if (!greaterThanMin()) {
    validates = false
    errors.value.push(`Minimum value is $${MINIMUM_AMOUNT}`)
  }

  if (!lessThanMax()) {
    validates = false
    errors.value.push(`Maximum value is $${MAXIMUM_AMOUNT}`)
  }

  return validates
}

function changedValues () {
  if (validates()) {
    __props.multiplaceGift.amounts = [lowerValue.value, middleValue.value, higherValue.value]
  }
}

</script>

<template>
  <div>
    <GInputError :error="errors"/>
    <div class="w-full grid grid-cols-3 lg:grid-cols-3 gap-4">
      <div>
        <GTextInput
          v-model.number="lowerValue"
          label="Lower Amount"
          placeholder="10"
          inputmode="numeric"
          type="number"
          :min="MINIMUM_AMOUNT"
          :max="MAXIMUM_AMOUNT"
          step="5"
          :errorTooltip="true"
          data-testid="lower-amount"
          @keypress="preventNonNumericInput"
          @input="changedValues"
        />
      </div>
      <div>
        <GTextInput
          v-model.number="middleValue"
          label="Middle Amount"
          placeholder="25"
          inputmode="numeric"
          type="number"
          :min="MINIMUM_AMOUNT"
          :max="MAXIMUM_AMOUNT"
          step="5"
          @keypress="preventNonNumericInput"
          @input="changedValues"
        />
      </div>
      <div>
        <GTextInput
          v-model.number="higherValue"
          label="Higher Amount"
          placeholder="50"
          inputmode="numeric"
          type="number"
          :min="MINIMUM_AMOUNT"
          :max="MAXIMUM_AMOUNT"
          step="5"
          @keypress="preventNonNumericInput"
          @input="changedValues"
        />
      </div>
    </div>
  </div>
</template>
